import { useCallback, useEffect, useState } from "react";
import { CommonResponseModel } from "../../../../../../../../../../../../../../Models/tableDetailsModel";
import { getApiErrorMessage } from "../../../../../../../../../../../../../../utils/getApiErrorMessage";
import agentsApi from "../../../../../../../../../../../../agents.api";
import { AvailableProductsAndCommissionsResponse } from "./addProduct.models";
import { isNoProductsAvailableResponse } from "./addProduct.utils";
import { GetAvailableProductsAndCommissions } from "../../../../../../../../../../../createDownlineModal/createDownline.services";

const productsAndCommissionsCache = new Map<number, AvailableProductsAndCommissionsResponse[]>();

interface UseAddProductProps {
    parentUserId: number | null;
}

export const useAddProduct = ({ parentUserId }: UseAddProductProps) => {
    const [productsAndCommissions, setProductsAndCommissions] = useState<
        AvailableProductsAndCommissionsResponse[]
    >(() => (parentUserId ? productsAndCommissionsCache.get(parentUserId) ?? [] : []));
    const [productLoading, setProductLoading] = useState(false);
    const [productFetchError, setProductFetchError] = useState(false);

    useEffect(() => {
        if (!parentUserId) {
            setProductsAndCommissions([]);
            setProductFetchError(false);
            return;
        }
        setProductsAndCommissions(productsAndCommissionsCache.get(parentUserId) ?? []);
        setProductFetchError(false);
    }, [parentUserId]);

    const fetchProductsAndCommissions = useCallback(async () => {
        if (!parentUserId) {
            setProductsAndCommissions([]);
            setProductFetchError(true);
            return { error: 'Missing parent user id' };
        }

        const cached = productsAndCommissionsCache.get(parentUserId);
        if (cached?.length) {
            setProductsAndCommissions(cached);
            setProductFetchError(false);
            return { data: cached };
        }

        const applyResponse = (response: CommonResponseModel<AvailableProductsAndCommissionsResponse[]>) => {
            if (response.flagStatus) {
                const products = response.value ?? [];
                if (products.length > 0) {
                    productsAndCommissionsCache.set(parentUserId, products);
                }
                setProductsAndCommissions(products);
                setProductFetchError(false);
                return { data: products };
            }

            if (isNoProductsAvailableResponse(response)) {
                setProductsAndCommissions([]);
                setProductFetchError(false);
                return { data: [] };
            }

            setProductsAndCommissions([]);
            setProductFetchError(true);
            return { error: response.error || 'Failed to fetch products' };
        };

        setProductLoading(true);
        setProductFetchError(false);

        try {
            const response = await GetAvailableProductsAndCommissions(parentUserId);
            return applyResponse(response);
        } catch (error) {
            setProductsAndCommissions([]);
            setProductFetchError(true);
            return { error: getApiErrorMessage(error, "Failed to fetch products") };
        } finally {
            setProductLoading(false);
        }
    }, [parentUserId]);

    return {
        productsAndCommissions,
        productLoading,
        fetchProductsAndCommissions,
        productFetchError,
    };
};
